import { useCallback, useEffect, useState } from 'react';

/**
 * Shared mobile-nav-open state for `AppShell` + `TopBar`, mirroring the
 * cross-instance `window` event bridge `useSidebarCollapsed` uses — same
 * pattern, deliberately WITHOUT `localStorage` persistence: the overlay
 * drawer is a transient, per-visit affordance (a mobile user reloading the
 * page should never find the nav stuck open from their last session),
 * whereas the desktop collapsed/expanded width is a real preference worth
 * remembering.
 */
const EVENT_NAME = 'qfly:mobile-nav-open-changed';

export interface MobileNavState {
  readonly open: boolean;
  readonly toggle: () => void;
  readonly close: () => void;
}

export const useMobileNavOpen = (): MobileNavState => {
  const [open, setOpenState] = useState(false);

  useEffect(() => {
    const handler = (e: Event): void => {
      setOpenState(Boolean((e as CustomEvent<boolean>).detail));
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const setOpen = useCallback((next: boolean): void => {
    setOpenState(next);
    window.dispatchEvent(new CustomEvent<boolean>(EVENT_NAME, { detail: next }));
  }, []);

  const toggle = useCallback((): void => setOpen(!open), [open, setOpen]);
  const close = useCallback((): void => setOpen(false), [setOpen]);

  return { open, toggle, close };
};
